/**
 * Convites de organização (backend: api/routes/organization_invitations.py).
 *
 * Regra de negócio: ninguém entra numa organização sem aceitar um convite (ou cadastrar a
 * própria empresa). Convidar não cria conta; a conta só é criada/vinculada no aceite.
 */
import apiService from "../shared/apiService";

export type InvitationRole = "ADMIN" | "EDITOR";

export interface OrganizationInvitation {
  id: number;
  email: string;
  org_role: InvitationRole;
  status: "pending" | "accepted" | "revoked" | "expired";
  invited_by: string | null;
  expires_at: string | null;
  created_at: string | null;
}

export interface PublicInvitation {
  organization_name: string;
  email: string;
  org_role: InvitationRole;
  invited_by: string | null;
  expires_at: string;
  /** Já existe conta com este e-mail — o aceite pede a senha dela em vez de criar outra. */
  account_exists: boolean;
}

export interface AcceptInvitationPayload {
  password?: string;
  name?: string;
  phone?: string;
}

class InvitationService {
  // --- Gestão (admin da organização) ---

  list(): Promise<{
    invitations: OrganizationInvitation[];
    seats_in_use: number;
    max_users: number;
  }> {
    return apiService.get("/organization/invitations");
  }

  create(
    email: string,
    role: InvitationRole,
  ): Promise<{ invitation: OrganizationInvitation; email_sent: boolean }> {
    return apiService.post("/organization/invitations", { email, role });
  }

  resend(id: number): Promise<{ email_sent: boolean }> {
    return apiService.post(`/organization/invitations/${id}/resend`, {});
  }

  revoke(id: number): Promise<{ success: boolean }> {
    return apiService.delete(`/organization/invitations/${id}`);
  }

  // --- Superusuário Virtus: convidar em nome de uma organização ---

  inviteAsPlatformAdmin(
    organizationId: number,
    email: string,
    role: InvitationRole,
  ): Promise<{ invitation: OrganizationInvitation; email_sent: boolean }> {
    return apiService.post(
      `/admin/organizations/${organizationId}/invitations`,
      {
        email,
        role,
      },
    );
  }

  // --- Públicas (página do link do e-mail) ---

  getByToken(token: string): Promise<{ invitation: PublicInvitation }> {
    return apiService.get(
      `/invitations/${encodeURIComponent(token)}`,
      undefined,
      {
        requiresAuth: false,
      },
    );
  }

  /**
   * `asLoggedUser`: aceita com a sessão atual (quando o usuário logado é o convidado).
   * Sem isso a chamada vai sem token — e um 401 (senha errada) não derruba a tela para o login.
   */
  accept(
    token: string,
    payload: AcceptInvitationPayload,
    asLoggedUser = false,
  ): Promise<{
    user: { id: number; email: string; name: string; org_role: string };
  }> {
    return apiService.post(
      `/invitations/${encodeURIComponent(token)}/accept`,
      payload,
      {
        requiresAuth: asLoggedUser,
      },
    );
  }
}

export const invitationService = new InvitationService();
export default invitationService;
